var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { useClasses, useLayoutProps, useProps } from '@hawkins/helpers';
import { useCombineRefs } from '../hooks';
import { useListItemWrapperStyles } from './listItemWrapper.styles';
function isListElement(el) {
    var tagName = el === null || el === void 0 ? void 0 : el.tagName;
    return tagName === 'UL' || tagName === 'OL';
}
var displayName = 'ListItemWrapper';
/**
 * `ListItemWrapper` dynamically wraps its children in an `li` tag
 * if the parent element of the root of the children is a list element (`ul` or `ol`) AND
 * the root of the children is NOT a list item element (`li`).
 *
 * Useful for maintaining valid HTML while building UI components with various ARIA roles set.
 *
 * @private
 */
export var ListItemWrapper = React.forwardRef(function (_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _b = useProps(displayName, props), classesProp = _b.classes, className = _b.className, _c = _b.disabled, disabled = _c === void 0 ? false : _c, layout = _b.layout, _d = _b.presentation, presentation = _d === void 0 ? false : _d, rootRef = _b.rootRef, styleProp = _b.style, restProps = __rest(_b, ["classes", "className", "disabled", "layout", "presentation", "rootRef", "style"]);
    // Refs
    var internalRef = React.useRef(null);
    var combinedRef = useCombineRefs([internalRef, ref]);
    // State
    var _e = __read(React.useState(false), 2), shouldWrap = _e[0], setShouldWrap = _e[1];
    React.useEffect(function () {
        if (disabled || !rootRef.current)
            return;
        if (shouldWrap) {
            if (rootRef.current.tagName === 'LI' ||
                (internalRef.current && !isListElement(internalRef.current.parentElement))) {
                setShouldWrap(false);
            }
        }
        else if (rootRef.current.tagName !== 'LI' && isListElement(rootRef.current.parentElement)) {
            setShouldWrap(true);
        }
    }, [disabled, rootRef, shouldWrap]);
    // Styles
    var baseClasses = useListItemWrapperStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root);
    return shouldWrap && !disabled ? (_jsx("li", __assign({ className: rootClassName, ref: combinedRef, role: presentation ? 'presentation' : undefined, style: style }, restProps, { children: children }))) : children;
});
ListItemWrapper.displayName = displayName;
